import { useMutation, useQueryClient } from '@tanstack/react-query';
import { conversationMessagesQueryKey } from 'hooks/useConversations';
import { addFailedMessage } from 'hooks/useFailedMessages';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import ApiPatientConversationsManager from 'services/api/ApiPatientConversationsManager';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { UserDTO } from 'services/api/models/UserDTO';
import { toast } from 'sonner';
import { ReplyingToMessage } from 'store/conversations/types';
import { selectCurrentUser } from 'store/users/selectors';

export type SendMessageVariables = {
  conversationId: string;
  messageText: string;
  replyingTo?: ReplyingToMessage;
};

// From the time, so that they do not collide with those of the unsent messages kept from an earlier session
let lastPendingMessageId = -Date.now();
/** Temporary ids of the messages being sent: negative, so they never collide with the server's. */
const nextPendingMessageId = () => --lastPendingMessageId;

/**
 * Sends a message to an existing conversation. It shows in the conversation as
 * soon as it is sent, marked as being sent; the server's confirmation turns it
 * into the real message, and if it fails it stays, marked as not sent, to be
 * sent again (see useFailedMessages).
 */
export default function useSendMessage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentUser = useSelector(selectCurrentUser);

  return useMutation({
    mutationFn: async ({ conversationId, messageText, replyingTo }: SendMessageVariables) => {
      const params = { conversationId, messageText, repliedToMessageId: replyingTo?.messageId };
      const result = currentUser?.is_caregiver
        ? await ApiDoctorConversationsManager.postMessageToDoctorConversation(params)
        : await ApiPatientConversationsManager.postMessageToPatientConversations(params);
      return result.message;
    },
    onMutate: ({ conversationId, messageText, replyingTo }) => {
      const pendingMessage: ConversationMessage = {
        id: nextPendingMessageId(),
        message_text: messageText,
        sender: currentUser as UserDTO,
        sent_at: new Date().toISOString(),
        replied_to_message: replyingTo && {
          id: replyingTo.messageId,
          message_text: replyingTo.messageText,
          sender: { display_name: replyingTo.senderName } as UserDTO,
        },
        pending: true,
      };
      queryClient.setQueryData<ConversationMessage[]>(conversationMessagesQueryKey(conversationId), prev =>
        prev ? [...prev, pendingMessage] : prev,
      );
      return { pendingMessage };
    },
    onSuccess: (message, { conversationId }, context) => {
      const pendingId = context?.pendingMessage.id;
      if (!message || !pendingId) return;
      queryClient.setQueryData<ConversationMessage[]>(conversationMessagesQueryKey(conversationId), prev =>
        // The websocket may have delivered it already (see Shell.tsx)
        prev?.some(m => m.id === message.id)
          ? prev.filter(m => m.id !== pendingId)
          : prev?.map(m => (m.id === pendingId ? message : m)),
      );
    },
    onError: (_error, { conversationId }, context) => {
      if (!context) return;
      const { pendingMessage } = context;
      queryClient.setQueryData<ConversationMessage[]>(conversationMessagesQueryKey(conversationId), prev =>
        prev?.filter(m => m.id !== pendingMessage.id),
      );
      void addFailedMessage(queryClient, { ...pendingMessage, conversationId });
      toast.error(t('chats.list.errors.sendMessage'));
    },
  });
}
